/* eslint-disable import/first */
import { flameTheme, lightTheme } from '@lightspeed/flame/Core';

import { HUB_EVENTS } from '~/main/namespace';
import { logger as pinoLogger, LoggerParams, LoggerScope } from '~/main/utils/logger';
import store from '~/renderer/store';
import { getEnabledLoggerScopes } from '~/renderer/store/hub/selectors';
import { isDebugMode } from '~/renderer/utils/config';

enum LogLevels {
  ERROR = 'ERROR',
  DEBUG = 'DEBUG',
}

type BadgeType = keyof typeof logBackgroundColor;

interface Badge {
  label: BadgeType;
}

const logBackgroundColor = {
  [LogLevels.DEBUG]: flameTheme.colors['blue-500'],
  [LogLevels.ERROR]: flameTheme.colors['maple-500'],
  [LoggerScope.APP]: '#CB3399',
  [LoggerScope.ABLY]: flameTheme.colors['green-500'],
  [LoggerScope.ABLY_CUSTOMER]: flameTheme.colors['green-500'],
  [LoggerScope.LOCAL_SERVER]: flameTheme.colors['green-500'],
  [LoggerScope.HUB]: flameTheme.colors['orange-500'],
  [LoggerScope.CFD]: '#019897',
  [LoggerScope.PRINTER]: '#653CCC',
  [LoggerScope.ELECTRON]: '#CB3399',
  [LoggerScope.ANALYTICS]: lightTheme.colors['yellow-300'],
  [LoggerScope.APPCUES]: lightTheme.colors['dive-100'],
  [LoggerScope.TIME]: lightTheme.colors.seal,
};

const getBadgeCSS = ({ label }: Badge) => {
  return `background: ${logBackgroundColor[label]}; color: white; padding: 3px; margin-left: 3px;`;
};

const getBadges = (
  level: LogLevels,
  scope: LoggerScope,
  transport?: LoggerScope.ABLY | LoggerScope.ABLY_CUSTOMER | LoggerScope.LOCAL_SERVER,
) => {
  const levelBadgeCSS = getBadgeCSS({ label: level });
  const levelTag = `%c${level}`;

  const transportBadgeCSS = transport ? getBadgeCSS({ label: transport } as Badge) : null;
  const transportTag = transport ? `%c${transport}` : transport;

  const scopeBadgeCSS = getBadgeCSS({ label: scope } as Badge);
  const scopeTag = `%c${scope}`;

  const timeBadgeCSS = getBadgeCSS({ label: LoggerScope.TIME });
  const timeTag = `%c[${new Date().toISOString()}]`;

  const tags = [timeTag, levelTag, transportTag, scopeTag].filter(Boolean);
  const css = [timeBadgeCSS, levelBadgeCSS, transportBadgeCSS, scopeBadgeCSS].filter(Boolean);

  return [tags.join(''), ...css];
};

interface DebugLogMessage {
  msg?: string;
  err?: Error | unknown;
  [key: string]: unknown;
}

interface ErrorLogMessage extends DebugLogMessage {
  err: Error | unknown;
}

const Logger = ({ scope, transport }: LoggerParams) => ({
  debug(log: DebugLogMessage): void {
    const enabledLoggerScopes = getEnabledLoggerScopes(store.getState());

    if (enabledLoggerScopes === 'none') return;
    if (Array.isArray(enabledLoggerScopes) && !enabledLoggerScopes.includes(scope)) {
      return;
    }

    window.ipcRenderer?.send(HUB_EVENTS.LOG, { level: 'debug', log, scope });
    if (!isDebugMode()) {
      return;
    }
    // eslint-disable-next-line
    console.group(...getBadges(LogLevels.DEBUG, scope, transport), log);
    pinoLogger({ scope }).debug(log);
    // eslint-disable-next-line
    console.groupEnd();
  },

  error({ err, ...log }: ErrorLogMessage): void {
    console.group(...getBadges(LogLevels.ERROR, scope));

    window.ipcRenderer?.send(HUB_EVENTS.LOG, { level: 'error', log: { ...log, err }, scope });
    pinoLogger({ scope }).error({ err, ...log });
  },
});

export default Logger;
